(function () {
	const MOBILE_MQ = window.matchMedia('(max-width: 749px)');

	const BORDER_CLASSES = [
		'hide_border',
		'is-row-start',
		'has-row-border',
		'clist-first-grid-row',
	];

	function isMobile() {
		return MOBILE_MQ.matches;
	}

	function getMaxPerRow(sectionEl) {
		if (!sectionEl) return 4;
		const raw = getComputedStyle(sectionEl).getPropertyValue('--per-row').trim();
		const parsed = parseInt(raw, 10);
		return Number.isNaN(parsed) || parsed < 1 ? 4 : Math.min(parsed, 4);
	}

	function getModeFromHost(host) {
		return host?.getAttribute('data-productis-in-row') || 'large';
	}

	/**
	 * Visible column count from flex breakpoints (template-collection-list-detailed.css,
	 * template-collection.css, collections-products-toggle.css).
	 */
	function getResponsiveColumnCount(mode, maxPerRow, viewportWidth, isProductGrid) {
		const w = viewportWidth ?? window.innerWidth;

		/* Products tab mobile override: gapless 2-col (1-col small ≤575). */
		if (isProductGrid && w <= 749) {
			if (w <= 575 && mode === 'small') return 1;
			return 2;
		}

		if (w <= 575) {
			return mode === 'small' ? 1 : 2;
		}
		if (w <= 989) {
			return mode === 'small' ? 2 : Math.min(3, maxPerRow);
		}
		if (w <= 1099) {
			return mode === 'small' ? 3 : maxPerRow;
		}
		return mode === 'small' ? 3 : maxPerRow;
	}

	function isProductGridHost(container, grid) {
		if (grid) {
			return Boolean(grid.querySelector('.collection-grid-card'));
		}
		if (!container) return false;
		return (
			container.id === 'ProductGridContainer' ||
			Boolean(container.closest && container.closest('#ProductGridContainer'))
		);
	}

	function getColumnCount(container, sectionEl, grid) {
		const mode = getModeFromHost(container);
		const maxPerRow = getMaxPerRow(sectionEl);
		return getResponsiveColumnCount(
			mode,
			maxPerRow,
			window.innerWidth,
			isProductGridHost(container, grid)
		);
	}

	function isVisibleCard(card) {
		if (!card) return false;
		if (card.classList.contains('hidden-by-filter')) return false;
		if (card.classList.contains('hidden-by-pagination')) return false;
		if (card.hidden) return false;
		if (card.getAttribute('aria-hidden') === 'true') return false;
		return card.offsetParent !== null || getComputedStyle(card).display !== 'none';
	}

	function clearCardBorders(card) {
		BORDER_CLASSES.forEach((name) => card.classList.remove(name));
		card.style.removeProperty('--clist-row-line-width');
	}

	function applyBorderClasses(cards, cols) {
		const visible = cards.filter(isVisibleCard);

		visible.forEach((card, index) => {
			clearCardBorders(card);

			const isLastInRow = (index + 1) % cols === 0;
			const isLastVisible = index === visible.length - 1;
			const isFirstInRow = index % cols === 0;
			const isFirstRow = index < cols;

			if (isLastInRow || isLastVisible) {
				card.classList.add('hide_border');
			}
			if (!isFirstRow) {
				card.classList.add('has-row-border');
			}
			if (isFirstInRow && !isFirstRow) {
				card.classList.add('is-row-start');
				card.style.setProperty('--clist-row-line-width', `${cols * 100}%`);
			}
			if (isFirstRow) {
				card.classList.add('clist-first-grid-row');
			}
		});
	}

	function getProductGrids(scope) {
		const root = scope || document;
		const grids = [];
		root.querySelectorAll('#ProductGridContainer #product-grid').forEach((grid) => {
			if (grid.querySelector('.collection-grid-card')) grids.push(grid);
		});
		return grids;
	}

	function getCollectionGrids(scope) {
		const root = scope || document;
		const grids = [];
		root
			.querySelectorAll(
				"[id^='CollectionListGrid-'], [data-clist-collections-grid] [id^='CollectionListGrid-'], [data-clist-collections-grid] .collection-product-list.collections-grid"
			)
			.forEach((grid) => {
				if (grid.querySelector('.border-item')) grids.push(grid);
			});
		return grids;
	}

	function updateGrid(grid) {
		if (!grid || !isMobile()) return;

		const sectionEl = grid.closest('[id^="shopify-section-"]');
		const host =
			grid.closest('[data-productis-in-row]') ||
			grid.closest('#ProductGridContainer') ||
			grid.closest('#CollectionListGridContainer') ||
			grid.closest('[data-clist-collections-grid]') ||
			sectionEl;

		const isProductGrid = Boolean(grid.querySelector('.collection-grid-card'));
		const cols = getColumnCount(host, sectionEl, grid);
		const selector = isProductGrid
			? 'li.collection-grid-card'
			: 'li.collections-grid__card.border-item';

		const cards = [...grid.querySelectorAll(selector)];
		grid.setAttribute('data-clist-mobile-borders', 'true');
		applyBorderClasses(cards, cols);
	}

	function clearGrid(grid) {
		if (!grid) return;
		grid.removeAttribute('data-clist-mobile-borders');
		/* Collection row borders use has-row-border / hide_border from collection-list-filters.js */
		if (!grid.querySelector('.collection-grid-card')) return;
		grid.querySelectorAll('li.collection-grid-card').forEach(clearCardBorders);
	}

	function clearCollectionGridMobileMode(grid) {
		if (!grid) return;
		grid.removeAttribute('data-clist-mobile-borders');
	}

	function updateScope(scope) {
		if (!isMobile()) {
			getProductGrids(scope).forEach(clearGrid);
			getCollectionGrids(scope).forEach(clearCollectionGridMobileMode);
			return;
		}

		getProductGrids(scope).forEach(updateGrid);
		getCollectionGrids(scope).forEach(updateGrid);
	}

	function updateAll() {
		document.querySelectorAll('[id^="shopify-section-"]').forEach((section) => {
			if (
				section.querySelector('[data-clist-shared-toolbar]') ||
				section.querySelector('.clist-all-products-toggle') ||
				section.querySelector('.collection-list--facets-vertical')
			) {
				updateScope(section);
			}
		});
	}

	let scheduled = false;
	function scheduleUpdate(scope) {
		if (scheduled) return;
		scheduled = true;
		requestAnimationFrame(() => {
			scheduled = false;
			if (scope) {
				updateScope(scope);
			} else {
				updateAll();
			}
		});
	}

	function bind() {
		document.addEventListener('click', (event) => {
			if (event.target.closest('.per-row__button')) {
				const scope = event.target.closest('[id^="shopify-section-"]');
				requestAnimationFrame(() => scheduleUpdate(scope));
			}
			if (event.target.closest('.js-load-more, .js-clist-load-more')) {
				const scope = event.target.closest('[id^="shopify-section-"]');
				setTimeout(() => scheduleUpdate(scope), 400);
			}
		});

		document.addEventListener('clist:products-injected', (e) => {
			const scope = e.detail?.sectionId
				? document.getElementById(`shopify-section-${e.detail.sectionId}`)
				: null;
			scheduleUpdate(scope);
		});
		document.addEventListener('clist:products-view-ready', (e) => {
			const scope = e.detail?.sectionId
				? document.getElementById(`shopify-section-${e.detail.sectionId}`)
				: null;
			scheduleUpdate(scope);
		});
		document.addEventListener('clist:products-grid-updated', () => scheduleUpdate());
		document.addEventListener('clist:collections-per-row-changed', (e) => {
			scheduleUpdate(e.detail?.sectionRoot || null);
		});

		let resizeTimer = null;
		window.addEventListener('resize', () => {
			clearTimeout(resizeTimer);
			resizeTimer = setTimeout(updateAll, 120);
		});

		if (typeof MOBILE_MQ.addEventListener === 'function') {
			MOBILE_MQ.addEventListener('change', () => updateAll());
		} else if (typeof MOBILE_MQ.addListener === 'function') {
			MOBILE_MQ.addListener(() => updateAll());
		}
	}

	window.ClistMobileGridBorders = {
		isMobile,
		getResponsiveColumnCount,
		getColumnCount,
		updateScope,
		updateAll,
		scheduleUpdate,
	};

	bind();

	if (document.readyState === 'loading') {
		document.addEventListener('DOMContentLoaded', updateAll);
	} else {
		updateAll();
	}

	document.addEventListener('shopify:section:load', () => scheduleUpdate());
})();
